package com.schoolconnect.app.feature.schools

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import com.schoolconnect.app.designsystem.AppDropdownMenu
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.text.input.KeyboardType
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppLabeledField
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.arrow_left
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.map_pin
import ischoolconnect.sharedui.generated.resources.phone_call
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import kotlinx.coroutines.yield
import org.jetbrains.compose.resources.painterResource

internal data class AddSchoolValidationErrors(
    val schoolName: String = "",
    val board: String = "",
    val type: String = "",
    val medium: String = "",
    val addressLine1: String = "",
    val city: String = "",
    val district: String = "",
    val state: String = "",
    val country: String = "",
    val pinCode: String = "",
    val schoolPhone: String = "",
    val schoolEmail: String = "",
    val primaryContact: String = "",
    val primaryPhone: String = "",
    val primaryEmail: String = "",
    val secondaryPhone: String = "",
    val secondaryEmail: String = ""
) {
    val hasAnyError: Boolean
        get() = listOf(
            schoolName,
            board,
            type,
            medium,
            addressLine1,
            city,
            district,
            state,
            country,
            pinCode,
            schoolPhone,
            schoolEmail,
            primaryContact,
            primaryPhone,
            primaryEmail,
            secondaryPhone,
            secondaryEmail
        ).any { it.isNotBlank() }
}

@Composable
internal fun AddSchoolScreen(
    onBack: () -> Unit,
    isSaving: Boolean = false,
    onSaveSchool: (SchoolEditorDraft) -> Unit
) {
    var schoolName by rememberSaveable { mutableStateOf("") }
    var board by rememberSaveable { mutableStateOf("") }
    var type by rememberSaveable { mutableStateOf("") }
    var medium by rememberSaveable { mutableStateOf("") }
    var addressLine1 by rememberSaveable { mutableStateOf("") }
    var addressLine2 by rememberSaveable { mutableStateOf("") }
    var city by rememberSaveable { mutableStateOf("") }
    var district by rememberSaveable { mutableStateOf("") }
    var state by rememberSaveable { mutableStateOf("") }
    var country by rememberSaveable { mutableStateOf("") }
    var pinCode by rememberSaveable { mutableStateOf("") }
    var schoolPhone by rememberSaveable { mutableStateOf("") }
    var schoolEmail by rememberSaveable { mutableStateOf("") }
    var primaryContact by rememberSaveable { mutableStateOf("") }
    var primaryPhone by rememberSaveable { mutableStateOf("") }
    var primaryEmail by rememberSaveable { mutableStateOf("") }
    var secondaryContact by rememberSaveable { mutableStateOf("") }
    var secondaryPhone by rememberSaveable { mutableStateOf("") }
    var secondaryEmail by rememberSaveable { mutableStateOf("") }
    var schoolDescription by rememberSaveable { mutableStateOf("") }
    var schoolLogoBytes by remember { mutableStateOf<ByteArray?>(null) }
    var schoolLogoFileName by rememberSaveable { mutableStateOf("") }
    var schoolLogoFileId by rememberSaveable { mutableStateOf("") }
    var submitted by rememberSaveable { mutableStateOf(false) }

    val errors = remember(
        submitted,
        schoolName,
        board,
        type,
        medium,
        addressLine1,
        city,
        district,
        state,
        country,
        pinCode,
        schoolPhone,
        schoolEmail,
        primaryContact,
        primaryPhone,
        primaryEmail,
        secondaryPhone,
        secondaryEmail
    ) {
        validateAddSchool(
            schoolName = schoolName,
            board = board,
            type = type,
            medium = medium,
            addressLine1 = addressLine1,
            city = city,
            district = district,
            state = state,
            country = country,
            pinCode = pinCode,
            schoolPhone = schoolPhone,
            schoolEmail = schoolEmail,
            primaryContact = primaryContact,
            primaryPhone = primaryPhone,
            primaryEmail = primaryEmail,
            secondaryPhone = secondaryPhone,
            secondaryEmail = secondaryEmail,
            showErrors = submitted
        )
    }

    var schoolBoardOptions by remember { mutableStateOf(emptyList<String>()) }
    var schoolTypeOptions by remember { mutableStateOf(emptyList<String>()) }
    var mediumOptions by remember { mutableStateOf(emptyList<String>()) }
    LaunchedEffect(Unit) {
        // The repository validates completeness and recovers missing
        // categories when an earlier aggregate response was truncated.
        SystemLookupRepository.refresh(forceRemote = false)
        // Ensure the selectors are populated from their authoritative
        // category endpoints even when the aggregate response is partial.
        schoolBoardOptions = SystemLookupRepository.loadCategoryItems("SCHOOL_BOARD")
            .getOrDefault(emptyList())
            .mapNotNull { it.displayValue?.trim()?.takeIf(String::isNotBlank) ?: it.code?.trim()?.takeIf(String::isNotBlank) }
        schoolTypeOptions = SystemLookupRepository.loadCategoryItems("SCHOOL_TYPE")
            .getOrDefault(emptyList())
            .mapNotNull { it.displayValue?.trim()?.takeIf(String::isNotBlank) ?: it.code?.trim()?.takeIf(String::isNotBlank) }
        mediumOptions = SystemLookupRepository.loadCategoryItems("SCHOOL_MEDIUM")
            .getOrDefault(emptyList())
            .mapNotNull { it.displayValue?.trim()?.takeIf(String::isNotBlank) ?: it.code?.trim()?.takeIf(String::isNotBlank) }
    }
    // Derive options from the shared snapshot after the single refresh above.
    // Loading each category independently here introduced a race with submit:
    // the user could select a value before serverCode() had the lookup mapping.

    val dismiss = { if (!isSaving) onBack() }
    AppDialogScaffold(
        onDismiss = dismiss,
        dialogWidthOverride = 1200.dp,
        maxDialogHeight = 1200.dp,
        fillDialogHeight = true,
        dismissOnClickOutside = false,
        shape = RoundedCornerShape(10.dp),
        isLoading = isSaving,
        loadingTitle = "Saving school"
    ) {
        AppDialogHeader(
            title = "Add School",
            subtitle = "Create a new school and add its details",
            onClose = dismiss
        )
        AppDialogDivider()
        AppDialogBody(
            compactBreakpoint = 900.dp,
            showVerticalScrollbar = true,
            fillAvailable = true,
            horizontalPadding = 24.dp,
            compactHorizontalPadding = 24.dp,
            verticalPadding = 24.dp
        ) { compact ->
                        SectionCard {
                            SchoolLogoPicker(
                                selectedFileName = schoolLogoFileName,
                                selectedBytes = schoolLogoBytes,
                                compact = compact,
                                onSelected = { fileName, bytes ->
                                    schoolLogoFileName = fileName
                                    schoolLogoBytes = bytes
                                    schoolLogoFileId = ""
                                }
                            )
                            if (compact) {
                                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                    FormInput(
                                        label = "School Name",
                                        required = true,
                                        value = schoolName,
                                        onValueChange = { schoolName = it },
                                        placeholder = "Enter school name",
                                        error = errors.schoolName
                                    )
                                    SelectInput(
                                        label = "Board",
                                        required = true,
                                        value = board,
                                        onValueChange = { board = it },
                                        placeholder = "Select board",
                                        options = schoolBoardOptions,
                                        error = errors.board
                                    )
                                    SelectInput(
                                        label = "Type",
                                        required = true,
                                        value = type,
                                        onValueChange = { type = it },
                                        placeholder = "Select school type",
                                        options = schoolTypeOptions,
                                        error = errors.type
                                    )
                                    SelectInput(
                                        label = "Medium",
                                        required = true,
                                        value = medium,
                                        onValueChange = { medium = it },
                                        placeholder = "Select medium",
                                        options = mediumOptions,
                                        error = errors.medium
                                    )
                                }
                            } else {
                                FormInput(
                                    label = "School Name",
                                    required = true,
                                    value = schoolName,
                                    onValueChange = { schoolName = it },
                                    placeholder = "Enter school name",
                                    error = errors.schoolName,
                                    modifier = Modifier.fillMaxWidth()
                                )

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    SelectInput(
                                        label = "Board",
                                        required = true,
                                        value = board,
                                        onValueChange = { board = it },
                                        placeholder = "Select board",
                                        options = schoolBoardOptions,
                                        error = errors.board,
                                        modifier = Modifier.weight(1f)
                                    )
                                    SelectInput(
                                        label = "Type",
                                        required = true,
                                        value = type,
                                        onValueChange = { type = it },
                                        placeholder = "Select school type",
                                        options = schoolTypeOptions,
                                        error = errors.type,
                                        modifier = Modifier.weight(1f)
                                    )
                                    SelectInput(
                                        label = "Medium",
                                        required = true,
                                        value = medium,
                                        onValueChange = { medium = it },
                                        placeholder = "Select medium",
                                        options = mediumOptions,
                                        error = errors.medium,
                                        modifier = Modifier.weight(1f)
                                    )
                                }

                            }
                        }

                        SectionCard {
                            if (compact) {
                                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                    FormInput(
                                        label = "Address Line 1",
                                        required = true,
                                        value = addressLine1,
                                        onValueChange = { addressLine1 = it },
                                        placeholder = "Enter address line 1",
                                        error = errors.addressLine1
                                    )
                                    FormInput(
                                        label = "Address Line 2",
                                        value = addressLine2,
                                        onValueChange = { addressLine2 = it },
                                        placeholder = "Enter address line 2 (optional)"
                                    )
                                    FormInput(
                                        label = "City",
                                        required = true,
                                        value = city,
                                        onValueChange = { city = it },
                                        placeholder = "Enter city",
                                        error = errors.city
                                    )
                                    FormInput(
                                        label = "District",
                                        required = true,
                                        value = district,
                                        onValueChange = { district = it },
                                        placeholder = "Enter district",
                                        error = errors.district
                                    )
                                    SearchableSchoolSelectInput(
                                        label = "State",
                                        required = true,
                                        value = state,
                                        onValueChange = { state = it },
                                        placeholder = "Select state",
                                        options = schoolStateOptions,
                                        error = errors.state
                                    )
                                    SearchableSchoolSelectInput(
                                        label = "Country",
                                        required = true,
                                        value = country,
                                        onValueChange = { country = it },
                                        placeholder = "Select country",
                                        options = schoolCountryOptions,
                                        error = errors.country
                                    )
                                    FormInput(
                                        label = "Pin Code",
                                        required = true,
                                        value = pinCode,
                                        onValueChange = { pinCode = it.filter(Char::isDigit).take(6) },
                                        placeholder = "Enter pin code",
                                        error = errors.pinCode
                                    )
                                }
                            } else {
                                FormInput(
                                    label = "Address Line 1",
                                    required = true,
                                    value = addressLine1,
                                    onValueChange = { addressLine1 = it },
                                    placeholder = "Enter address line 1",
                                    error = errors.addressLine1
                                )
                                FormInput(
                                    label = "Address Line 2",
                                    value = addressLine2,
                                    onValueChange = { addressLine2 = it },
                                    placeholder = "Enter address line 2 (optional)"
                                )
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    FormInput(
                                        label = "City",
                                        required = true,
                                        value = city,
                                        onValueChange = { city = it },
                                        placeholder = "Enter city",
                                        error = errors.city,
                                        modifier = Modifier.weight(1f)
                                    )
                                    FormInput(
                                        label = "District",
                                        required = true,
                                        value = district,
                                        onValueChange = { district = it },
                                        placeholder = "Enter district",
                                        error = errors.district,
                                        modifier = Modifier.weight(1f)
                                    )
                                    SearchableSchoolSelectInput(
                                        label = "State",
                                        required = true,
                                        value = state,
                                        onValueChange = { state = it },
                                        placeholder = "Select state",
                                        options = schoolStateOptions,
                                        error = errors.state,
                                        modifier = Modifier.weight(1f)
                                    )
                                }
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    SearchableSchoolSelectInput(
                                        label = "Country",
                                        required = true,
                                        value = country,
                                        onValueChange = { country = it },
                                        placeholder = "Select country",
                                        options = schoolCountryOptions,
                                        error = errors.country,
                                        modifier = Modifier.weight(1f)
                                    )
                                    FormInput(
                                        label = "Pin Code",
                                        required = true,
                                        value = pinCode,
                                        onValueChange = { pinCode = it.filter(Char::isDigit).take(6) },
                                        placeholder = "Enter pin code",
                                        error = errors.pinCode,
                                        modifier = Modifier.weight(1f)
                                    )
                                    Spacer(modifier = Modifier.weight(1f))
                                }
                            }
                        }

                        SectionCard {
                            if (compact) {
                                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                    FormInput(
                                        label = "School Phone",
                                        required = true,
                                        value = schoolPhone,
                                        onValueChange = { schoolPhone = sanitizeSchoolPhoneInput(it) },
                                        placeholder = "Enter school phone",
                                        error = errors.schoolPhone,
                                        keyboardType = KeyboardType.Phone
                                    )
                                    FormInput(
                                        label = "School Email",
                                        required = true,
                                        value = schoolEmail,
                                        onValueChange = { schoolEmail = it },
                                        placeholder = "Enter school email",
                                        error = errors.schoolEmail,
                                        keyboardType = KeyboardType.Email
                                    )
                                    SelectContactFields(
                                        primaryContact = primaryContact,
                                        onPrimaryContactChange = { primaryContact = it },
                                        primaryContactError = errors.primaryContact,
                                        primaryPhone = primaryPhone,
                                        onPrimaryPhoneChange = { primaryPhone = sanitizeSchoolPhoneInput(it) },
                                        primaryPhoneError = errors.primaryPhone,
                                        primaryEmail = primaryEmail,
                                        onPrimaryEmailChange = { primaryEmail = it },
                                        primaryEmailError = errors.primaryEmail,
                                        secondaryContact = secondaryContact,
                                        onSecondaryContactChange = { secondaryContact = it },
                                        secondaryPhone = secondaryPhone,
                                        onSecondaryPhoneChange = { secondaryPhone = sanitizeSchoolPhoneInput(it) },
                                        secondaryPhoneError = errors.secondaryPhone,
                                        secondaryEmail = secondaryEmail,
                                        onSecondaryEmailChange = { secondaryEmail = it },
                                        secondaryEmailError = errors.secondaryEmail,
                                        compact = true
                                    )
                                }
                            } else {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    FormInput(
                                        label = "School Phone",
                                        required = true,
                                        value = schoolPhone,
                                        onValueChange = { schoolPhone = sanitizeSchoolPhoneInput(it) },
                                        placeholder = "Enter school phone",
                                        error = errors.schoolPhone,
                                        keyboardType = KeyboardType.Phone,
                                        modifier = Modifier.weight(1f)
                                    )
                                    FormInput(
                                        label = "School Email",
                                        required = true,
                                        value = schoolEmail,
                                        onValueChange = { schoolEmail = it },
                                        placeholder = "Enter school email",
                                        error = errors.schoolEmail,
                                        keyboardType = KeyboardType.Email,
                                        modifier = Modifier.weight(2f)
                                    )
                                }

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    FormInput(
                                        label = "Primary Contact",
                                        required = true,
                                        value = primaryContact,
                                        onValueChange = { primaryContact = it },
                                        placeholder = "Enter primary contact name",
                                        error = errors.primaryContact,
                                        modifier = Modifier.weight(1f)
                                    )
                                    FormInput(
                                        label = "Primary Phone",
                                        required = true,
                                        value = primaryPhone,
                                        onValueChange = { primaryPhone = sanitizeSchoolPhoneInput(it) },
                                        placeholder = "Enter phone number",
                                        error = errors.primaryPhone,
                                        modifier = Modifier.weight(1f)
                                    )
                                    FormInput(
                                        label = "Primary Email",
                                        required = true,
                                        value = primaryEmail,
                                        onValueChange = { primaryEmail = it },
                                        placeholder = "Enter primary email",
                                        error = errors.primaryEmail,
                                        keyboardType = KeyboardType.Email,
                                        modifier = Modifier.weight(1f)
                                    )
                                }

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    FormInput(
                                        label = "Secondary Contact",
                                        value = secondaryContact,
                                        onValueChange = { secondaryContact = it },
                                        placeholder = "Enter secondary contact name",
                                        modifier = Modifier.weight(1f)
                                    )
                                    FormInput(
                                        label = "Secondary Phone",
                                        value = secondaryPhone,
                                        onValueChange = { secondaryPhone = sanitizeSchoolPhoneInput(it) },
                                        placeholder = "Enter phone number (optional)",
                                        error = errors.secondaryPhone,
                                        modifier = Modifier.weight(1f)
                                    )
                                    FormInput(
                                        label = "Secondary Email",
                                        value = secondaryEmail,
                                        onValueChange = { secondaryEmail = it },
                                        placeholder = "Enter email (optional)",
                                        error = errors.secondaryEmail,
                                        keyboardType = KeyboardType.Email,
                                        modifier = Modifier.weight(1f)
                                    )
                                }
                            }

                        }

                        FormTextArea(
                            label = "School Comments",
                            value = schoolDescription,
                            onValueChange = { schoolDescription = it },
                            placeholder = "Enter school comments (optional)"
                        )
        }
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction(
                    label = "Cancel",
                    onClick = dismiss,
                    enabled = !isSaving
                ),
                AppDialogFooterAction(
                    label = if (isSaving) "Saving..." else "Save",
                    style = AppDialogActionStyle.Primary,
                    enabled = !isSaving,
                    onClick = {
                        val submissionErrors = validateAddSchool(
                            schoolName = schoolName,
                            board = board,
                            type = type,
                            medium = medium,
                            addressLine1 = addressLine1,
                            city = city,
                            district = district,
                            state = state,
                            country = country,
                            pinCode = pinCode,
                            schoolPhone = schoolPhone,
                            schoolEmail = schoolEmail,
                            primaryContact = primaryContact,
                            primaryPhone = primaryPhone,
                            primaryEmail = primaryEmail,
                            secondaryPhone = secondaryPhone,
                            secondaryEmail = secondaryEmail,
                            showErrors = true
                        )
                        submitted = true
                        if (!submissionErrors.hasAnyError) {
                            onSaveSchool(
                                SchoolEditorDraft(
                                    name = schoolName.trim(),
                                    code = "",
                                    board = board.trim(),
                                    type = type.trim(),
                                    medium = medium.trim(),
                                    addressLine1 = addressLine1.trim(),
                                    addressLine2 = addressLine2.trim(),
                                    city = city.trim(),
                                    district = district.trim(),
                                    state = state.trim(),
                                    country = country.trim(),
                                    pinCode = pinCode.trim(),
                                    schoolPhone = schoolPhone.trim(),
                                    schoolEmail = schoolEmail.trim(),
                                    contactName = primaryContact.trim(),
                                    primaryPhone = primaryPhone.trim(),
                                    primaryEmail = primaryEmail.trim(),
                                    secondaryContactName = secondaryContact.trim(),
                                    secondaryPhone = secondaryPhone.trim(),
                                    secondaryEmail = secondaryEmail.trim(),
                                    schoolLogoBytes = schoolLogoBytes,
                                    schoolLogoFileName = schoolLogoFileName,
                                    attachmentFileId = schoolLogoFileId,
                                    schoolDescription = schoolDescription.trim(),
                                    schoolLogoUrl = "",
                                    students = "0"
                                )
                            )
                        }
                    }
                )
            ),
            contentPadding = PaddingValues(horizontal = 24.dp)
        )
    }
}

@Composable
private fun SectionCard(
    content: @Composable () -> Unit
) {
    val sectionBorder = Color(0xFFE2E8F0)
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
        colors = CardDefaults.cardColors(containerColor = SchoolsSurface),
        border = BorderStroke(1.dp, sectionBorder),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            content()
        }
    }
}

@Composable
private fun FieldRow(
    label: String,
    required: Boolean = false,
    field: @Composable () -> Unit,
    error: String = ""
) {
    AppLabeledField(
        label = label,
        required = required,
        errorText = error,
        verticalSpacing = 6.dp,
        labelStyle = MaterialTheme.typography.labelMedium
    ) {
        field()
    }
}

@Composable
private fun FormInput(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    required: Boolean = false,
    error: String = "",
    keyboardType: KeyboardType = KeyboardType.Text,
    modifier: Modifier = Modifier
) {
    AppLabeledField(
        label = label,
        required = required,
        errorText = error,
        verticalSpacing = 6.dp,
        labelStyle = MaterialTheme.typography.labelMedium,
        modifier = modifier
    ) {
        FieldSurface(
            value = value,
            onValueChange = onValueChange,
            placeholder = placeholder,
            error = error,
            keyboardType = keyboardType
        )
    }
}

@Composable
private fun SelectInput(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    options: List<String>,
    required: Boolean = false,
    error: String = "",
    modifier: Modifier = Modifier
) {
    var expanded by remember { mutableStateOf(false) }
    val selectionScope = rememberCoroutineScope()
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val anchorWidth = with(LocalDensity.current) { anchorSize.width.toDp() }

    AppLabeledField(
        label = label,
        required = required,
        errorText = error,
        verticalSpacing = 6.dp,
        labelStyle = MaterialTheme.typography.labelMedium,
        modifier = modifier
    ) {
        Box(modifier = Modifier.onGloballyPositioned { anchorSize = it.size }) {
            Surface(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(40.dp)
                    .clickable(
                        indication = null,
                        interactionSource = remember { MutableInteractionSource() },
                        onClick = { expanded = true }
                ),
                shape = RoundedCornerShape(6.dp),
                color = Color.White,
                border = androidx.compose.foundation.BorderStroke(
                    1.dp,
                    if (error.isNotBlank()) SchoolsRed else SchoolsInputBorder
                )
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(horizontal = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = value.ifBlank { placeholder },
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Normal,
                        color = if (value.isBlank()) SchoolsTextSecondary else SchoolsTextPrimary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Icon(
                        painter = painterResource(Res.drawable.chevron_down),
                        contentDescription = null,
                        tint = SchoolsTextPrimary,
                        modifier = Modifier.size(20.dp)
                    )
                }
            }

            AppDropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                width = anchorWidth.takeIf { it > 0.dp }
            ) {
                options.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option) },
                        onClick = {
                            // Keep the popup item attached until Compose JS
                            // finishes its click interaction. Updating the
                            // field immediately can detach the item while
                            // the pointer handler is still reading its
                            // CompositionLocal (and crash the web app).
                            selectionScope.launch {
                                yield()
                                delay(100)
                                expanded = false
                                onValueChange(option)
                            }
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun FieldSurface(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    error: String = "",
    keyboardType: KeyboardType = KeyboardType.Text
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(6.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(
            1.dp,
            if (error.isNotBlank()) SchoolsRed else SchoolsInputBorder
        )
    ) {
        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            singleLine = true,
            textStyle = TextStyle(
                fontSize = 14.sp,
                fontWeight = FontWeight.Normal,
                color = SchoolsTextPrimary
            ),
            keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
            cursorBrush = SolidColor(SchoolsBlue),
            modifier = Modifier
                .fillMaxWidth()
                .height(40.dp)
                .padding(horizontal = 12.dp)
        ) { innerTextField ->
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.CenterStart
            ) {
                if (value.isBlank()) {
                    Text(
                        text = placeholder,
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Normal,
                        color = SchoolsTextSecondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                innerTextField()
            }
        }
    }
}

@Composable
private fun FormTextArea(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    required: Boolean = false,
    error: String = "",
    modifier: Modifier = Modifier
) {
    AppLabeledField(
        label = label,
        required = required,
        errorText = error,
        verticalSpacing = 6.dp,
        labelStyle = MaterialTheme.typography.labelMedium,
        modifier = modifier
    ) {
        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(6.dp),
            color = Color.White,
            border = androidx.compose.foundation.BorderStroke(
                1.dp,
                if (error.isNotBlank()) SchoolsRed else SchoolsInputBorder
            )
        ) {
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                singleLine = false,
                maxLines = 3,
                textStyle = TextStyle(
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Normal,
                    color = SchoolsTextPrimary
                ),
                cursorBrush = SolidColor(SchoolsBlue),
                modifier = Modifier
                    .fillMaxWidth()
                    .height(78.dp)
                    .padding(horizontal = 12.dp, vertical = 10.dp)
            ) { innerTextField ->
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.TopStart
                ) {
                    if (value.isBlank()) {
                        Text(
                            text = placeholder,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Normal,
                            color = SchoolsTextSecondary,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    innerTextField()
                }
            }
        }
    }
}

@Composable
private fun SchoolInfoBanner(message: String) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(10.dp),
        color = Color(0xFFFFF4E6),
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFFFD8A8))
    ) {
        Text(
            text = message,
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
            style = MaterialTheme.typography.bodyMedium,
            color = Color(0xFF9A5B00),
            fontWeight = FontWeight.SemiBold
        )
    }
}

@Composable
private fun SelectContactFields(
    primaryContact: String,
    onPrimaryContactChange: (String) -> Unit,
    primaryContactError: String,
    primaryPhone: String,
    onPrimaryPhoneChange: (String) -> Unit,
    primaryPhoneError: String,
    primaryEmail: String,
    onPrimaryEmailChange: (String) -> Unit,
    primaryEmailError: String,
    secondaryContact: String,
    onSecondaryContactChange: (String) -> Unit,
    secondaryPhone: String,
    onSecondaryPhoneChange: (String) -> Unit,
    secondaryPhoneError: String,
    secondaryEmail: String,
    onSecondaryEmailChange: (String) -> Unit,
    secondaryEmailError: String,
    compact: Boolean
) {
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            FormInput(
                label = "Primary Contact",
                required = true,
                value = primaryContact,
                onValueChange = onPrimaryContactChange,
                placeholder = "Enter primary contact name",
                error = primaryContactError
            )
            FormInput(
                label = "Primary Phone",
                required = true,
                value = primaryPhone,
                onValueChange = { onPrimaryPhoneChange(sanitizeSchoolPhoneInput(it)) },
                placeholder = "Enter phone number",
                error = primaryPhoneError,
                keyboardType = KeyboardType.Phone
            )
            FormInput(
                label = "Primary Email",
                required = true,
                value = primaryEmail,
                onValueChange = onPrimaryEmailChange,
                placeholder = "Enter primary email",
                error = primaryEmailError,
                keyboardType = KeyboardType.Email
            )
            FormInput(
                label = "Secondary Contact",
                value = secondaryContact,
                onValueChange = onSecondaryContactChange,
                placeholder = "Enter secondary contact name"
            )
            FormInput(
                label = "Secondary Phone",
                value = secondaryPhone,
                onValueChange = { onSecondaryPhoneChange(sanitizeSchoolPhoneInput(it)) },
                placeholder = "Enter phone number (optional)",
                error = secondaryPhoneError,
                keyboardType = KeyboardType.Phone
            )
            FormInput(
                label = "Secondary Email",
                value = secondaryEmail,
                onValueChange = onSecondaryEmailChange,
                placeholder = "Enter email (optional)",
                error = secondaryEmailError,
                keyboardType = KeyboardType.Email
            )
        }
        return
    }

    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            FormInput(
                label = "Primary Contact",
                required = true,
                value = primaryContact,
                onValueChange = onPrimaryContactChange,
                placeholder = "Enter primary contact name",
                error = primaryContactError,
                modifier = Modifier.weight(1f)
            )
            FormInput(
                label = "Primary Phone",
                required = true,
                value = primaryPhone,
                onValueChange = { onPrimaryPhoneChange(sanitizeSchoolPhoneInput(it)) },
                placeholder = "Enter phone number",
                error = primaryPhoneError,
                keyboardType = KeyboardType.Phone,
                modifier = Modifier.weight(1f)
            )
            FormInput(
                label = "Primary Email",
                required = true,
                value = primaryEmail,
                onValueChange = onPrimaryEmailChange,
                placeholder = "Enter primary email",
                error = primaryEmailError,
                keyboardType = KeyboardType.Email,
                modifier = Modifier.weight(1f)
            )
        }

        Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            FormInput(
                label = "Secondary Contact",
                value = secondaryContact,
                onValueChange = onSecondaryContactChange,
                placeholder = "Enter secondary contact name",
                modifier = Modifier.weight(1f)
            )
            FormInput(
                label = "Secondary Phone",
                value = secondaryPhone,
                onValueChange = { onSecondaryPhoneChange(sanitizeSchoolPhoneInput(it)) },
                placeholder = "Enter phone number (optional)",
                error = secondaryPhoneError,
                keyboardType = KeyboardType.Phone,
                modifier = Modifier.weight(1f)
            )
            FormInput(
                label = "Secondary Email",
                value = secondaryEmail,
                onValueChange = onSecondaryEmailChange,
                placeholder = "Enter email (optional)",
                error = secondaryEmailError,
                keyboardType = KeyboardType.Email,
                modifier = Modifier.weight(1f)
            )
        }
    }
}

private fun validateAddSchool(
    schoolName: String,
    board: String,
    type: String,
    medium: String,
    addressLine1: String,
    city: String,
    district: String,
    state: String,
    country: String,
    pinCode: String,
    schoolPhone: String,
    schoolEmail: String,
    primaryContact: String,
    primaryPhone: String,
    primaryEmail: String,
    secondaryPhone: String,
    secondaryEmail: String,
    showErrors: Boolean
): AddSchoolValidationErrors {
    if (!showErrors) return AddSchoolValidationErrors()

    fun required(value: String, message: String) = if (value.isBlank()) message else ""
    fun optionalEmail(value: String): String {
        val trimmed = value.trim()
        if (trimmed.isBlank()) return ""
        return if (trimmed.matches(emailRegex)) "" else "Enter a valid email"
    }
    fun requiredEmail(value: String, message: String): String {
        val trimmed = value.trim()
        if (trimmed.isBlank()) return message
        return if (trimmed.matches(emailRegex)) "" else "Enter a valid email"
    }
    fun phone(value: String, required: Boolean): String {
        val normalized = value.filterNot { it.isWhitespace() || it == '-' || it == '(' || it == ')' }
        if (normalized.isBlank()) return if (required) "Phone number is required" else ""
        return if (normalized.matches(phoneRegex)) "" else "Enter a valid phone number"
    }
    fun schoolPhoneError(value: String): String {
        if (value.isBlank()) return "School phone is required"
        return if (isValidSchoolPhoneNumber(value)) "" else "Enter a valid Indian mobile or landline number"
    }
    fun optionalSchoolPhoneError(value: String): String =
        if (value.isBlank()) "" else if (isValidSchoolPhoneNumber(value)) "" else "Enter a valid Indian mobile or landline number"

    return AddSchoolValidationErrors(
        schoolName = required(schoolName, "School name is required"),
        board = required(board, "Board is required"),
        type = required(type, "Type is required"),
        medium = required(medium, "Medium is required"),
        addressLine1 = required(addressLine1, "Address line 1 is required"),
        city = required(city, "City is required"),
        district = required(district, "District is required"),
        state = required(state, "State is required"),
        country = required(country, "Country is required"),
        pinCode = required(pinCode, "Pin code is required"),
        schoolPhone = schoolPhoneError(schoolPhone),
        schoolEmail = if (schoolEmail.isBlank()) "School email is required" else optionalEmail(schoolEmail).ifBlank { "" },
        primaryContact = required(primaryContact, "Primary contact is required"),
        primaryPhone = if (primaryPhone.isBlank()) "Primary phone is required" else schoolPhoneError(primaryPhone),
        primaryEmail = requiredEmail(primaryEmail, "Primary email is required"),
        secondaryPhone = optionalSchoolPhoneError(secondaryPhone),
        secondaryEmail = optionalEmail(secondaryEmail)
    )
}

private val emailRegex = Regex("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$")
private val phoneRegex = Regex("^(?:\\+91)?[6-9]\\d{9}$")
private val SchoolsInputBorder = Color(0xFFE5E7EB)
